<script setup lang="ts">
/**
 * Appoint Secretary(비서 선임, B32103) 스튜디오 패키지. Stage 1 DR(1부) · Stage 2 Form 45B(선임 비서별).
 *   Form 45B 는 DocScope(f45b_{id}/) 로 인물별 격리. DR 선임 목록은 각 스코프값에서 도출.
 */
import { computed, ref, watch, provide } from 'vue';
import DocScope from '@/features/startTask/my_preset/sg/aura/agm/cdd/DocScope.vue';
import { useAgmFill } from '@/features/startTask/my_preset/sg/aura/agm/cdd/useCddFill';
import SecretaryDrDoc from './SecretaryDrDoc.vue';
import Form45BDoc from './Form45BDoc.vue';

const props = defineProps<{ autoLocal?: { newIds?: number[] } | null }>();
const fill = useAgmFill();

const newIds = ref<number[]>([1]);
let newSeq = 1;

watch(() => props.autoLocal, (v) => {
  if (!v || !Array.isArray(v.newIds)) return;
  newIds.value = [...v.newIds];
  newSeq = v.newIds.length ? Math.max(...v.newIds) : 0;
}, { deep: true, immediate: true });

const st = (v: unknown) => String(v ?? '').trim();
provide('secPeople', computed(() => ({
  news: newIds.value
    .map((id) => ({ name: st(fill.values[`f45b_${id}/secretary_name`]), nric: st(fill.values[`f45b_${id}/secretary_identification_no`]) }))
    .filter((r) => r.name),
})));

function add() { newSeq += 1; newIds.value.push(newSeq); }
function remove(id: number) { newIds.value = newIds.value.filter((x) => x !== id); }

const currentStage = ref(1);
const TOTAL_STAGES = 2;
function go(n: number) { if (n >= 1 && n <= TOTAL_STAGES) currentStage.value = n; }
const totalDocs = computed(() => 1 + newIds.value.length);

const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }

function gatherLocalState() { return { newIds: [...newIds.value], currentStage: currentStage.value }; }
function restoreLocalState(s: { newIds?: number[]; currentStage?: number } | null) {
  if (!s) return;
  if (Array.isArray(s.newIds) && s.newIds.length) { newIds.value = [...s.newIds]; newSeq = Math.max(...s.newIds); }
  if (s.currentStage) currentStage.value = s.currentStage;
}
defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="sec-pkg">
    <div class="stepper">
      <button type="button" class="step" :class="{ active: currentStage === 1, done: currentStage > 1 }" @click="go(1)">
        <span class="step-no">{{ currentStage > 1 ? '✓' : 1 }}</span>
        <span class="step-txt"><b>Stage 1</b><small>DR</small></span>
      </button>
      <button type="button" class="step" :class="{ active: currentStage === 2 }" @click="go(2)">
        <span class="step-no">2</span>
        <span class="step-txt"><b>Stage 2</b><small>Form 45B</small></span>
      </button>
    </div>
    <div class="zoom-bar">
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>
    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <section v-show="currentStage === 1" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 1</span><span class="stage-t">DR (Appoint Secretary).docx</span></div>
        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">📄</span><span class="nm">DR (Appoint Secretary).docx</span>
            <span class="no">Doc 1 / {{ totalDocs }}</span>
          </div>
          <div class="simple-frame"><SecretaryDrDoc /></div>
        </div>
      </section>

      <section v-show="currentStage === 2" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 2</span><span class="stage-t">Form 45B.docx (per appointed secretary)</span></div>
        <div class="wd-toolbar">
          <span class="wd-toolbar-lbl">Add copy (one per secretary)</span>
          <button type="button" class="wd-tool" @click="add">+ Add secretary</button>
        </div>
        <div v-if="!newIds.length" class="wd-empty">No secretaries — nothing generated for this stage. Use <b>+ Add secretary</b> above.</div>
        <div v-for="(id, i) in newIds" :key="id" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">🧾</span><span class="nm">Form 45B.docx</span>
            <span class="copy">#{{ id }}</span>
            <button type="button" class="wd-rm" title="Remove" @click="remove(id)">×</button>
            <span class="no">Doc {{ i + 2 }} / {{ totalDocs }}</span>
          </div>
          <div class="simple-frame">
            <DocScope :scope="`f45b_${id}/`"><Form45BDoc /></DocScope>
          </div>
        </div>
      </section>
    </div>
    <div class="stage-nav">
      <button v-if="currentStage > 1" type="button" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ TOTAL_STAGES }}</span>
      <button v-if="currentStage < TOTAL_STAGES" type="button" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style scoped>
.sec-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20,30,60,0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; }
.step:hover { background: #eef3fb; } .step.active { background: #eaf1ff; border-color: #b9d0ff; } .step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; } .step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; } .step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; } .step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; } .doc-zoom-btn { min-width: 36px; font-size: 15px; } .doc-zoom-btn:disabled { opacity: 0.35; cursor: default; } .doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; } .stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }
.wd-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.wd-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; }
.wd-tool { border: 1px solid #1b84ff; background: #fff; color: #1b84ff; border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer; } .wd-tool:hover { background: #eaf3ff; }
.pkg-doc { width: 100%; } .pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; } .pkg-doclabel .ic { font-size: 14px; } .pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; } .pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.wd-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; }
.wd-empty { width: 100%; margin: 8px 0 18px; padding: 16px 18px; border: 1px dashed #c5d0e0; border-radius: 12px; background: #f7f9fc; color: #5b6b82; font-size: 13px; box-sizing: border-box; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 16px; width: 794px; max-width: 100%; margin: 36px auto 12px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; } .nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; } .nav-btn:disabled { opacity: 0.55; cursor: default; }
.nav-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
</style>
